import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Lock, PlayCircle, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { SiteLayout, useDocumentTitle } from '../components/platform/SiteLayout';
import { Container, Heading, Card, Btn, Spinner, ErrorState, PlainText, StatusBadge } from '../components/platform/ui';
import useAsync from '../hooks/useAsync';
import { platformApi, errorText } from '../services/platformApi';
import { subscriptionAPI } from '../utils/auth';
import { useAuth } from '../contexts/AuthContext';
import { goToPayment, formatPrice, formatDate } from '../lib/pay';
import { LLink, useLang } from '../lib/lang';

const CoursePage = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { lang, to } = useLang();
  const { data: c, loading, error, reload } = useAsync(() => platformApi.course(slug), [slug, user?.id, i18n.language]);
  const [busy, setBusy] = useState(false);
  useDocumentTitle(c?.title);

  const buy = async () => {
    if (!user) { toast.info(t('pl.product.loginToPay')); navigate(to('/login')); return; }
    setBusy(true);
    try {
      const res = await subscriptionAPI.createOrder({ courseId: c.id, email: user.email });
      goToPayment(res.payment);
    } catch (err) {
      setBusy(false);
      toast.error(errorText(err, t('pl.product.orderError')));
    }
  };

  if (loading) return <SiteLayout><Container className="py-12"><Spinner /></Container></SiteLayout>;
  if (error) {
    const nf = error.response?.status === 404;
    return <SiteLayout><Container className="py-12"><ErrorState text={nf ? t('pl.courses.notFound') : t('pl.courses.loadError')} onRetry={nf ? undefined : reload} /></Container></SiteLayout>;
  }

  const firstOpen = c.lessons.find((l) => !l.locked);
  const progress = c.progress;
  return (
    <SiteLayout>
      <Container className="py-10 grid lg:grid-cols-[1fr,340px] gap-10">
        <div className="space-y-8 min-w-0">
          <div>
            <div className="flex gap-2 mb-3">
              <StatusBadge tone="blue">{c.kind === 'webinar' ? t('pl.courses.webinar') : t('pl.courses.course')}</StatusBadge>
              {c.is_free && <StatusBadge tone="green">{t('pl.common.free')}</StatusBadge>}
              {c.starts_at && <StatusBadge>{formatDate(c.starts_at, lang)}</StatusBadge>}
            </div>
            <Heading level={1} className="text-4xl mb-3">{c.title}</Heading>
            {c.author_name && <p className="text-gray-600 mb-3">{t('pl.courses.author', { name: c.author_name })}</p>}
            <p className="text-lg text-gray-700">{c.summary}</p>
          </div>
          {c.cover_url && <img src={c.cover_url} alt={c.title} className="w-full border-2 border-black" />}
          {c.description && <section><Heading level={2} className="text-2xl mb-3">{t('pl.courses.description')}</Heading><PlainText text={c.description} /></section>}
          {c.program?.length > 0 && (
            <section><Heading level={2} className="text-2xl mb-3">{t('pl.courses.program')}</Heading>
              <ol className="list-decimal pl-6 space-y-1">{c.program.map((x, i) => <li key={i}>{x}</li>)}</ol></section>
          )}
          <section>
            <Heading level={2} className="text-2xl mb-3">{t('pl.courses.lessons')}</Heading>
            {c.lessons.length === 0 ? <p className="text-gray-600">{t('pl.courses.noLessons')}</p> : (
              <ul className="divide-y divide-black/20 border-2 border-black bg-[#F5F1E8]">
                {c.lessons.map((l) => (
                  <li key={l.id} className="p-3 flex items-center gap-3">
                    {l.completed ? <CheckCircle2 size={20} className="text-green-700 shrink-0" /> : l.locked ? <Lock size={20} className="text-gray-500 shrink-0" /> : <PlayCircle size={20} className="text-[#C41E3A] shrink-0" />}
                    {l.locked
                      ? <span className="text-gray-500 flex-1">{l.position}. {l.title} <span className="text-xs">{t('pl.courses.unavailable')}</span></span>
                      : <LLink to={`/webinars/${c.slug}/lessons/${l.id}`} className="flex-1 hover:text-[#C41E3A]">{l.position}. {l.title}</LLink>}
                    {l.is_free_preview && !c.has_access && <StatusBadge tone="green">{t('pl.courses.freeFragment')}</StatusBadge>}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
        <aside>
          <Card className="lg:sticky lg:top-24">
            {c.has_access ? (
              <>
                <p className="font-bold mb-1">{c.is_free ? t('pl.courses.isFree') : t('pl.courses.accessOpen')}</p>
                {progress && <p className="text-sm text-gray-600 mb-3">{t('pl.courses.progress', { done: progress.done, total: progress.total })}</p>}
                {firstOpen && <Btn to={`/webinars/${c.slug}/lessons/${firstOpen.id}`} className="w-full">{t('pl.courses.start')}</Btn>}
              </>
            ) : (
              <>
                <p className="text-4xl font-bold text-[#C41E3A] mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>{formatPrice(c.price, lang)}</p>
                <p className="text-sm text-gray-600 mb-4">{t('pl.courses.oneTime')}</p>
                <Btn onClick={buy} loading={busy} className="w-full"><CreditCard size={18} />{t('pl.common.buy')}</Btn>
                {firstOpen && <Btn variant="secondary" to={`/webinars/${c.slug}/lessons/${firstOpen.id}`} className="w-full mt-3">{t('pl.courses.freePreview')}</Btn>}
              </>
            )}
          </Card>
        </aside>
      </Container>
    </SiteLayout>
  );
};

export default CoursePage;
